import {
  createProceduralCueBuffer,
  createProceduralNoiseLoop,
  getProceduralCueSpec,
} from './procedural.ts';
import { DEFAULT_RIDE_AUDIO_SETTINGS, normalizeRideAudioSettings } from './settings.ts';
import type {
  AudioVisibilitySource,
  RideAudioController,
  RideAudioEnvironment,
  RideAudioEvent,
  RideAudioEventType,
  RideAudioFrame,
  RideAudioSettings,
  RideAudioSettingsPatch,
  RideAudioSnapshot,
  RideAudioState,
  RideAudioUnlockResult,
} from './types.ts';

const VOICE_POOL_SIZE = 12;

interface CueVoice {
  readonly gain: GainNode;
  source: AudioBufferSourceNode | null;
}

interface AudioGraph {
  readonly master: GainNode;
  readonly rideBus: GainNode;
  readonly windBus: GainNode;
  readonly uiBus: GainNode;
  readonly windSource: AudioBufferSourceNode;
  readonly windFilter: BiquadFilterNode;
  readonly windLevel: GainNode;
  readonly wheelSource: OscillatorNode;
  readonly wheelFilter: BiquadFilterNode;
  readonly wheelLevel: GainNode;
  readonly rideVoices: CueVoice[];
  readonly uiVoices: CueVoice[];
}

function defaultContextFactory(): AudioContext | null {
  const environment = globalThis as typeof globalThis & {
    webkitAudioContext?: typeof AudioContext;
  };
  const Context = environment.AudioContext ?? environment.webkitAudioContext;
  return Context ? new Context() : null;
}

function defaultVisibility(): AudioVisibilitySource | null {
  return typeof document === 'undefined' ? null : document;
}

function safeErrorMessage(error: unknown): string {
  return error instanceof Error ? error.message : 'Audio could not be started.';
}

function clampSpeed(value: number): number {
  return Number.isFinite(value) ? Math.min(120, Math.max(0, value)) : 0;
}

function setTarget(parameter: AudioParam, value: number, now: number, duration = 0.04): void {
  parameter.cancelScheduledValues(now);
  parameter.setTargetAtTime(value, now, duration);
}

export class WebRideAudioController implements RideAudioController {
  private readonly createContext: () => AudioContext | null;
  private readonly visibility: AudioVisibilitySource | null;
  private readonly visibilityListener: EventListener;
  private context: AudioContext | null = null;
  private graph: AudioGraph | null = null;
  private state: RideAudioState = 'locked';
  private settings: RideAudioSettings;
  private frame: RideAudioFrame = {
    speedMetersPerSecond: 0,
    sectionType: null,
    running: false,
  };
  private lastError: string | null = null;
  private unlockInFlight: Promise<RideAudioUnlockResult> | null = null;
  private suspendedByVisibility = false;
  private readonly cueBuffers = new Map<RideAudioEventType, AudioBuffer>();
  private readonly lastCueTimes = new Map<string, number>();

  constructor(
    initialSettings: RideAudioSettingsPatch = {},
    environment: RideAudioEnvironment = {},
  ) {
    this.settings = normalizeRideAudioSettings(initialSettings, DEFAULT_RIDE_AUDIO_SETTINGS);
    this.createContext = environment.createContext ?? defaultContextFactory;
    this.visibility =
      environment.visibility === undefined ? defaultVisibility() : environment.visibility;
    this.visibilityListener = () => {
      void this.syncVisibility();
    };
    this.visibility?.addEventListener('visibilitychange', this.visibilityListener);
  }

  get snapshot(): RideAudioSnapshot {
    return {
      state: this.state,
      settings: { ...this.settings },
      lastError: this.lastError,
      activeVoices: this.countActiveVoices(),
    };
  }

  unlock(): Promise<RideAudioUnlockResult> {
    if (this.unlockInFlight) return this.unlockInFlight;
    this.unlockInFlight = this.performUnlock().finally(() => {
      this.unlockInFlight = null;
    });
    return this.unlockInFlight;
  }

  private async performUnlock(): Promise<RideAudioUnlockResult> {
    if (this.state === 'disposed') {
      return { ok: false, state: 'disposed', reason: 'Audio has been disposed.' };
    }
    if (this.state === 'unavailable') {
      return { ok: false, state: 'unavailable', reason: this.lastError ?? 'Audio is unavailable.' };
    }

    try {
      if (!this.context) {
        this.context = this.createContext();
        if (!this.context) {
          this.state = 'unavailable';
          this.lastError = 'Web Audio is unavailable in this browser.';
          return { ok: false, state: 'unavailable', reason: this.lastError };
        }
      }
      if (!this.graph) this.graph = this.createGraph(this.context);
      if (this.context.state !== 'running') await this.context.resume();
      this.state = this.context.state === 'running' ? 'ready' : 'suspended';
      this.lastError = null;
      this.applySettings();
      this.applyFrame();
      if (this.visibility?.hidden) await this.syncVisibility();
      return { ok: true, state: this.state === 'ready' ? 'ready' : 'suspended' };
    } catch (error) {
      this.state = 'failed';
      this.lastError = safeErrorMessage(error);
      return { ok: false, state: 'failed', reason: this.lastError };
    }
  }

  update(frame: RideAudioFrame): void {
    this.frame = {
      speedMetersPerSecond: clampSpeed(frame.speedMetersPerSecond),
      sectionType: frame.sectionType,
      running: Boolean(frame.running),
    };
    this.applyFrame();
  }

  emit(event: RideAudioEvent): boolean {
    const context = this.context;
    const graph = this.graph;
    if (!context || !graph || this.state !== 'ready') return false;

    const spec = getProceduralCueSpec(event.type);
    const dedupeKey = `${event.type}:${event.sectionId ?? ''}`;
    const lastTime = this.lastCueTimes.get(dedupeKey) ?? Number.NEGATIVE_INFINITY;
    if (context.currentTime - lastTime < spec.cooldownSeconds) return false;

    const voices = spec.category === 'ui' ? graph.uiVoices : graph.rideVoices;
    const voice = voices.find((candidate) => candidate.source === null);
    if (!voice) return false;

    let buffer = this.cueBuffers.get(event.type);
    if (!buffer) {
      buffer = createProceduralCueBuffer(context, event.type);
      this.cueBuffers.set(event.type, buffer);
    }
    const source = context.createBufferSource();
    source.buffer = buffer;
    source.connect(voice.gain);
    const intensity = Number.isFinite(event.intensity) ? Math.max(0, event.intensity ?? 1) : 1;
    setTarget(voice.gain.gain, Math.min(1, spec.gain * intensity), context.currentTime, 0.005);
    voice.source = source;
    source.onended = () => {
      source.disconnect();
      if (voice.source === source) voice.source = null;
    };
    this.lastCueTimes.set(dedupeKey, context.currentTime);
    source.start();
    return true;
  }

  setSettings(patch: RideAudioSettingsPatch): RideAudioSettings {
    this.settings = normalizeRideAudioSettings(patch, this.settings);
    this.applySettings();
    return { ...this.settings };
  }

  async syncVisibility(): Promise<void> {
    const context = this.context;
    if (!context || this.state === 'disposed') return;

    if (this.visibility?.hidden) {
      if (context.state === 'running') {
        this.suspendedByVisibility = true;
        try {
          await context.suspend();
        } catch (error) {
          this.lastError = safeErrorMessage(error);
        }
      }
      if (this.state !== 'failed' && this.state !== 'unavailable') this.state = 'suspended';
      return;
    }

    if (!this.suspendedByVisibility) return;
    this.suspendedByVisibility = false;
    try {
      await context.resume();
      this.state = context.state === 'running' ? 'ready' : 'suspended';
      if (this.state === 'ready') {
        this.lastError = null;
        this.applySettings();
        this.applyFrame();
      }
    } catch (error) {
      this.state = 'suspended';
      this.lastError = safeErrorMessage(error);
    }
  }

  dispose(): void {
    if (this.state === 'disposed') return;
    this.visibility?.removeEventListener('visibilitychange', this.visibilityListener);
    const graph = this.graph;
    if (graph) {
      graph.windSource.stop();
      graph.wheelSource.stop();
      for (const voice of [...graph.rideVoices, ...graph.uiVoices]) {
        voice.source?.stop();
        voice.source?.disconnect();
        voice.source = null;
        voice.gain.disconnect();
      }
      graph.windSource.disconnect();
      graph.windFilter.disconnect();
      graph.windLevel.disconnect();
      graph.wheelSource.disconnect();
      graph.wheelFilter.disconnect();
      graph.wheelLevel.disconnect();
      graph.rideBus.disconnect();
      graph.windBus.disconnect();
      graph.uiBus.disconnect();
      graph.master.disconnect();
    }
    if (this.context && this.context.state !== 'closed') {
      void this.context.close().catch(() => undefined);
    }
    this.graph = null;
    this.context = null;
    this.cueBuffers.clear();
    this.lastCueTimes.clear();
    this.state = 'disposed';
  }

  private createGraph(context: AudioContext): AudioGraph {
    const master = context.createGain();
    const rideBus = context.createGain();
    const windBus = context.createGain();
    const uiBus = context.createGain();
    rideBus.connect(master);
    windBus.connect(master);
    uiBus.connect(master);
    master.connect(context.destination);

    const windSource = context.createBufferSource();
    const windFilter = context.createBiquadFilter();
    const windLevel = context.createGain();
    windSource.buffer = createProceduralNoiseLoop(context);
    windSource.loop = true;
    windFilter.type = 'lowpass';
    windSource.connect(windFilter);
    windFilter.connect(windLevel);
    windLevel.connect(windBus);
    windSource.start();

    const wheelSource = context.createOscillator();
    const wheelFilter = context.createBiquadFilter();
    const wheelLevel = context.createGain();
    wheelSource.type = 'square';
    wheelFilter.type = 'lowpass';
    wheelSource.connect(wheelFilter);
    wheelFilter.connect(wheelLevel);
    wheelLevel.connect(rideBus);
    wheelSource.start();

    const makeVoices = (bus: GainNode): CueVoice[] =>
      Array.from({ length: VOICE_POOL_SIZE }, () => {
        const gain = context.createGain();
        gain.gain.value = 0;
        gain.connect(bus);
        return { gain, source: null };
      });

    return {
      master,
      rideBus,
      windBus,
      uiBus,
      windSource,
      windFilter,
      windLevel,
      wheelSource,
      wheelFilter,
      wheelLevel,
      rideVoices: makeVoices(rideBus),
      uiVoices: makeVoices(uiBus),
    };
  }

  private applySettings(): void {
    const context = this.context;
    const graph = this.graph;
    if (!context || !graph || context.state === 'closed') return;
    const now = context.currentTime;
    setTarget(graph.master.gain, this.settings.muted ? 0 : this.settings.masterVolume, now);
    setTarget(graph.rideBus.gain, this.settings.rideVolume, now);
    setTarget(graph.windBus.gain, this.settings.windVolume, now);
    setTarget(graph.uiBus.gain, this.settings.uiVolume, now);
  }

  private applyFrame(): void {
    const context = this.context;
    const graph = this.graph;
    if (!context || !graph || context.state === 'closed') return;
    const speed = this.frame.running ? this.frame.speedMetersPerSecond : 0;
    const normalizedSpeed = Math.min(1, speed / 45);
    const now = context.currentTime;
    const sectionMultiplier =
      this.frame.sectionType === 'lift'
        ? 0.58
        : this.frame.sectionType === 'brake'
          ? 1.7
          : this.frame.sectionType === 'launch'
            ? 1.25
            : this.frame.sectionType === 'station'
              ? 0.35
              : 1;

    setTarget(graph.windLevel.gain, Math.pow(normalizedSpeed, 1.3) * 0.62, now, 0.08);
    setTarget(graph.windFilter.frequency, 450 + normalizedSpeed * 5_400, now, 0.08);
    setTarget(graph.windSource.playbackRate, 0.55 + normalizedSpeed * 1.2, now, 0.08);
    setTarget(
      graph.wheelLevel.gain,
      speed > 0.5 ? Math.min(0.22, 0.025 + normalizedSpeed * 0.18) : 0,
      now,
      0.05,
    );
    setTarget(graph.wheelSource.frequency, (7 + speed * 1.9) * sectionMultiplier, now, 0.04);
    setTarget(graph.wheelFilter.frequency, 260 + normalizedSpeed * 1_300, now, 0.06);
  }

  private countActiveVoices(): number {
    if (!this.graph) return 0;
    return [...this.graph.rideVoices, ...this.graph.uiVoices].filter(
      (voice) => voice.source !== null,
    ).length;
  }
}

export function createRideAudioController(
  initialSettings: RideAudioSettingsPatch = {},
  environment: RideAudioEnvironment = {},
): RideAudioController {
  return new WebRideAudioController(initialSettings, environment);
}
